import { useCallback } from "react";
import useTrackEvent from "@hooks/useTrackEvent";
import type { LiveDealCard } from "./helpers";
import type { LiveDealFundGroup } from "./fundGroups";

export interface LiveDealsAnalytics {
  trackFilterClicked: (
    filterType: "currency" | "status",
    filterValue: string
  ) => void;
  trackDealCardClicked: (card: LiveDealCard, isHeld: boolean) => void;
  trackFundCardClicked: (group: LiveDealFundGroup) => void;
}

/**
 * The live deals list, on the same event the mobile list reports. Web filters
 * on status where mobile filters on deal type, so `filter_type` is what tells
 * the two axes apart rather than a separate event.
 */
export const useLiveDealsAnalytics = (): LiveDealsAnalytics => {
  const track = useTrackEvent("liveDealsInteraction");

  const trackFilterClicked = useCallback(
    (filterType: "currency" | "status", filterValue: string) => {
      track({
        action: "filter_clicked",
        filter_type: filterType,
        filter_value: filterValue,
      });
    },
    [track]
  );

  const trackDealCardClicked = useCallback(
    (card: LiveDealCard, isHeld: boolean) => {
      track({
        action: "card_clicked",
        card_type: "deal",
        card_id: card.id,
        card_name: card.name,
        deal_type: card.dealType,
        is_live: card.isLive,
        is_held: isHeld,
      });
    },
    [track]
  );

  const trackFundCardClicked = useCallback(
    (group: LiveDealFundGroup) => {
      track({
        action: "card_clicked",
        card_type: "fund",
        card_id: group.investableId,
        card_name: group.name,
        deal_type: group.tenors[0]?.card.dealType,
        is_live: group.isLive,
        tenor_count: group.tenors.length,
      });
    },
    [track]
  );

  return { trackFilterClicked, trackDealCardClicked, trackFundCardClicked };
};
